/**
 * @fileoverview Hook utilitário responsável por atrasar a propagação de valores e reduzir chamadas frequentes.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook para fazer debounce de um valor
 * Útil para evitar chamadas excessivas de API ou re-renders
 *
 * @param {*} value - O valor a ser debounced
 * @param {number} delay - Tempo de delay em ms (padrão: 500ms)
 * @returns {*} - O valor com debounce aplicado
 */
export function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Define um timer que atualiza o valor após o delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Limpa o timer se o value mudar antes do delay acabar
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook para criar uma função com debounce
 * Útil para handlers de eventos como onChange, onScroll, etc.
 *
 * @param {Function} callback - A função a ser executada
 * @param {number} delay - Tempo de delay em ms (padrão: 500ms)
 * @returns {Function} - A função com debounce aplicado
 */
export function useDebouncedCallback(callback, delay = 500) {
  const timeoutIdRef = useRef(null);

  // Cleanup ao desmontar componente
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  return useCallback((...args) => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
}

export default useDebounce;
